/**
 * H15 — Home Menu drawer data
 * Figma RedisignHomePage `50:10284` desktop (440×995) · `50:10382` mobile (393×852)
 * Components: MenuNav `50:9865` · MenuTab `50:9840` · ListRow Type=Menu `50:9620` / `50:9625`
 * · Modal `50:10099` · Modal content/Language `50:9893` · Currency `50:10127`
 */

import type { RouteLocationRaw } from 'vue-router'
import type {
  CategoryNodeSearch,
  CategoryTreeAudience,
  CategoryTreeLang,
  CategoryTreeNodeV1,
  CategoryTreeResponseV1,
} from '@/features/search/api/categories'
import { resolveAudience, type Audience } from '@/helpers/audience'
import { SEARCH_ROUTE } from '@/router/routes'
import { FAIR_SITE_HEADER_DEFAULT_AUDIENCE } from '@/ui/tokens/fair-site-header.data'
import {
  FAIR_LOCALE_CURRENCY_DEFAULT,
  persistCurrency,
  readStoredCurrency,
} from '@/ui/tokens/fair-locale-menu.data'

/** Figma Drawer 440 wide on desktop; mobile is full width (393 frame). */
export const FAIR_HOME_V2_MENU_DRAWER_WIDTH_DESKTOP = 440

/** Gender tabs — same items/values as `FairSiteHeader` (Women / Men / Kids). */
export const FAIR_HOME_V2_MENU_GENDER_TABS = FAIR_SITE_HEADER_DEFAULT_AUDIENCE

/** The results page's URL parameter a menu click carries (CATEGORY-TREE). */
export const FAIR_HOME_V2_MENU_CATEGORY_NODE_PARAM = 'category_node'

/**
 * Arbitrary-depth node. `children` is `[]` for a leaf — a row without chevron
 * that navigates straight to results (designer: "every row without a chevron
 * is a leaf").
 *
 * `search` is what the backend told us to open the results page with; the
 * front passes it through and never rebuilds it from the label.
 */
export interface FairHomeV2MenuNode {
  id: string
  label: string
  /** The `total` the results page will show for this row. */
  count: number
  search: CategoryNodeSearch
  children: FairHomeV2MenuNode[]
}

function toMenuNode(node: CategoryTreeNodeV1): FairHomeV2MenuNode {
  return {
    id: node.slug,
    label: node.name,
    count: node.count,
    search: node.search,
    children: (node.children ?? []).map(toMenuNode),
  }
}

/**
 * The v1 tree (`GET /api/v1/categories/tree`) as the drawer's recursive
 * rows. A department (`groups`) is a row with a chevron whose rows are its
 * nodes; a root the backend put in `flat` is a leaf row at the root. The
 * audience is the TAB the tree was requested for, never a row.
 */
export function toFairHomeV2MenuNodes(
  tree: CategoryTreeResponseV1 | null | undefined,
): FairHomeV2MenuNode[] {
  if (!tree) return []
  const groups = (tree.groups ?? []).map((group) => ({
    id: group.key,
    label: group.name,
    count: group.count,
    search: group.search,
    children: (group.nodes ?? []).map(toMenuNode),
  }))
  const flat = (tree.flat ?? []).map(toMenuNode)
  return [...groups, ...flat]
}

export function isFairHomeV2MenuLeaf(node: FairHomeV2MenuNode): boolean {
  return node.children.length === 0
}

/** The header tab value (`women` / `woman` / `men` …) as the tree's audience. */
export function toFairHomeV2MenuTreeAudience(audience: string): CategoryTreeAudience {
  const resolved: Audience = resolveAudience(audience)
  return resolved === 'unisex' ? 'woman' : resolved
}

/** The UI locale as the tree's label language; anything else reads English. */
export function toFairHomeV2MenuTreeLang(locale: string | null | undefined): CategoryTreeLang {
  return locale === 'ru' ? 'ru' : 'en'
}

/**
 * Leaf / View all → Results Page.
 *
 * The page is opened with the node's `search` parameters verbatim:
 * `?category_node=<slug>&audience=<tab>`, no search text. The results page
 * turns the slug into `filters.categories: ["n.<slug>"]` and the backend
 * binds the node's own hard clause — the same one it counted the row with,
 * so the number on the row is the number on the page.
 */
export function buildFairHomeV2MenuLocation(
  path: FairHomeV2MenuNode[],
  audience: string,
): RouteLocationRaw | null {
  const target = path[path.length - 1]
  if (!target) return null
  return {
    path: SEARCH_ROUTE.replace('/:searchText?', ''),
    query: {
      audience: resolveAudience(audience),
      [FAIR_HOME_V2_MENU_CATEGORY_NODE_PARAM]: target.search.category_node,
    },
  }
}

/* ---------------------------------------------------------------------- */
/* Footer — Language / Currency (Modal `50:10099`)                         */
/* ---------------------------------------------------------------------- */

export type FairHomeV2MenuLocaleKind = 'language' | 'currency'

export interface FairHomeV2MenuLanguageOption {
  /** BCP-47-ish code, also the stored value. */
  id: string
  /** Native name — primary label (Figma "English", "Français" …). */
  label: string
  /** English name — secondary caption. */
  description: string
  /** App locale to switch to when we actually have translations. */
  locale?: 'en' | 'ru'
  /** Short value shown in the footer row (Figma "EN"). */
  short: string
}

/** Figma `50:9893` — 8 languages, exact order. */
export const FAIR_HOME_V2_MENU_LANGUAGES: FairHomeV2MenuLanguageOption[] = [
  { id: 'en', label: 'English', description: 'English', locale: 'en', short: 'EN' },
  { id: 'fr', label: 'Français', description: 'French', short: 'FR' },
  { id: 'it', label: 'Italiano', description: 'Italian', short: 'IT' },
  { id: 'de', label: 'Deutsch', description: 'German', short: 'DE' },
  { id: 'es', label: 'Español', description: 'Spanish', short: 'ES' },
  { id: 'ru', label: 'Русский', description: 'Russian', locale: 'ru', short: 'RU' },
  { id: 'ar', label: 'العربية', description: 'Arabic', short: 'AR' },
  { id: 'zh', label: '简体中文', description: 'Chinese (Simplified)', short: 'ZH' },
]

export interface FairHomeV2MenuCurrencyOption {
  /** ISO code, stored value (same storage as legacy `FairLocaleMenu`). */
  id: string
  /** Figma "USD $" — code + symbol. */
  label: string
  /** Figma secondary caption. */
  description: string
}

/** Figma `50:10127` — exact order. */
export const FAIR_HOME_V2_MENU_CURRENCIES: FairHomeV2MenuCurrencyOption[] = [
  { id: 'USD', label: 'USD $', description: 'US Dollar' },
  { id: 'EUR', label: 'EUR €', description: 'Euro' },
  { id: 'AED', label: 'AED د.إ', description: 'UAE Dirham' },
  { id: 'SAR', label: 'SAR ﷼', description: 'Saudi Riyal' },
  { id: 'CNY', label: 'CNY ¥', description: 'Chinese Yuan' },
  { id: 'RUB', label: 'RUB ₽', description: 'Russian Ruble' },
]

/**
 * Preferred UI language (8 options) is wider than the locales we ship
 * (`en` / `ru`). Store the preference separately so the choice survives even
 * when it cannot switch `vue-i18n` yet.
 */
export const FAIR_HOME_V2_MENU_LANGUAGE_STORAGE_KEY = 'fair-home-v2-menu-language'

export function readFairHomeV2MenuLanguage(fallback = 'en'): string {
  if (typeof window === 'undefined') return fallback
  const stored = window.localStorage.getItem(FAIR_HOME_V2_MENU_LANGUAGE_STORAGE_KEY)
  if (stored && FAIR_HOME_V2_MENU_LANGUAGES.some((option) => option.id === stored)) {
    return stored
  }
  return fallback
}

export function persistFairHomeV2MenuLanguage(id: string) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(FAIR_HOME_V2_MENU_LANGUAGE_STORAGE_KEY, id)
}

export function readFairHomeV2MenuCurrency(): string {
  const stored = readStoredCurrency(FAIR_LOCALE_CURRENCY_DEFAULT)
  return FAIR_HOME_V2_MENU_CURRENCIES.some((option) => option.id === stored)
    ? stored
    : FAIR_LOCALE_CURRENCY_DEFAULT
}

export function persistFairHomeV2MenuCurrency(id: string) {
  persistCurrency(id)
}

export function findFairHomeV2MenuLanguage(id: string): FairHomeV2MenuLanguageOption {
  return (
    FAIR_HOME_V2_MENU_LANGUAGES.find((option) => option.id === id)
    ?? FAIR_HOME_V2_MENU_LANGUAGES[0]
  )
}

export function findFairHomeV2MenuCurrency(id: string): FairHomeV2MenuCurrencyOption {
  return (
    FAIR_HOME_V2_MENU_CURRENCIES.find((option) => option.id === id)
    ?? FAIR_HOME_V2_MENU_CURRENCIES[0]
  )
}
